import { ChevronDownIcon } from "@radix-ui/react-icons";
import { CircleAlert, Pencil, RefreshCw } from "lucide-react";
import { memo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { Language, ReportSectionMeta } from "~/api/model";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { LoadingSpinner } from "~/components/Icons";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { UnifiedMarkdown } from "~/components/UnifiedMarkdown";
import { useChatDocumentParams } from "~/helpers";
import { useLazyRender } from "~/hooks/useLazyRender";
import { trackEvent } from "~/utils/analytics";
import { BatesLink, SectionLink } from "~/utils/markdownTerms";
import { Hint } from "../../types";
import { EditReportSectionDialog } from "./EditReportSectionDialog";
import { localizeReportSectionTitle } from "./localizeReportSectionTitle";
import { ReportSectionSources, SourceReference } from "./ReportSectionSources";
import { useSectionData } from "./useSectionData";

type ReportSectionViewProps = {
  reportSection: ReportSectionMeta;
  accordionValue: string;
  terms: Hint[];
  sectionLinks?: SectionLink[];
  reportId: string;
  isExpanded: boolean;
  templateName?: string | null;
  outputLanguage?: Language | null;
  isActionAvailable?: boolean;
  onSourceClick?: (source: SourceReference) => void;
  onSectionClick?: (sectionId: string, firstPageId: string) => void;
  batesLinks?: BatesLink[];
  onBatesClick?: (pageId: string) => void;
};

export const ReportSectionView = memo(
  ({
    reportSection,
    accordionValue,
    reportId,
    isExpanded,
    templateName,
    outputLanguage,
    isActionAvailable = true,
    onSourceClick,
  }: ReportSectionViewProps) => {
    const { formatMessage } = useIntl();
    const { organizationId, caseId } = useChatDocumentParams();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const { ref: lazyRef, hasBeenVisible } = useLazyRender("400px");

    const sectionId = reportSection.id;

    const { sectionData, handleRegenerate, handleEdit } = useSectionData(
      organizationId,
      caseId,
      reportId,
      sectionId,
      reportSection.status,
      isExpanded,
      hasBeenVisible
    );

    const sources = (sectionData?.sources ?? []) as SourceReference[];

    const isProcessing =
      reportSection.status === "processing" ||
      reportSection.status === "pending";
    const isSectionFailed = reportSection.status === "failed";

    const displayTitle = localizeReportSectionTitle(
      reportSection.title,
      templateName,
      outputLanguage
    );

    const renderSectionContent = () => {
      if (isSectionFailed) {
        return (
          <Alert
            variant="default"
            className="border-red-200 bg-red-50 px-3 py-1.5"
          >
            <div className="flex items-center gap-2">
              <CircleAlert className="h-4 w-4 text-red-500 shrink-0" />
              <AlertDescription className="flex-1 text-red-800 text-xs">
                {formatMessage({ id: "chat.report.sectionFailed" })}
              </AlertDescription>
              {isActionAvailable && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="shrink-0 text-red-700 hover:bg-red-100 hover:text-red-900 gap-1"
                  onClick={handleRegenerate}
                >
                  <RefreshCw className="h-3 w-3" />
                  <FormattedMessage
                    id="report.rewrite"
                    defaultMessage="Rewrite"
                  />
                </Button>
              )}
            </div>
          </Alert>
        );
      }

      return (
        <div ref={lazyRef}>
          {hasBeenVisible ? (
            <>
              <UnifiedMarkdown>{sectionData?.content ?? ""}</UnifiedMarkdown>
              {sources.length > 0 && (
                <ReportSectionSources
                  sources={sources}
                  onSourceClick={onSourceClick}
                />
              )}
            </>
          ) : (
            <div className="animate-pulse min-h-[150px]">
              <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[330px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[300px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-2.5" />
            </div>
          )}
        </div>
      );
    };

    return (
      <AccordionItem
        value={accordionValue}
        className="group border-none relative"
        disabled={isProcessing}
      >
        <div className="sticky top-0 z-10 bg-background pt-1 -mt-1">
          <AccordionTrigger className="py-0 hover:no-underline" hideChevron>
            <h2 className="text-sm font-semibold">
              {displayTitle}
              {isProcessing ? (
                <LoadingSpinner className="inline-block size-4 ml-1.5 align-middle text-muted-foreground" />
              ) : (
                <ChevronDownIcon className="inline-block size-4 ml-1.5 align-middle shrink-0 transition-transform group-data-[state=open]:rotate-180" />
              )}
            </h2>
          </AccordionTrigger>
        </div>
        <AccordionContent className="pb-0 pt-2">
          {renderSectionContent()}
        </AccordionContent>
        {!isProcessing && !isSectionFailed && (
          <div className="h-8 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 mt-0">
            <div className="inline-flex items-center gap-2">
              <ContentCopyButton
                content={sectionData?.content ?? ""}
                preCopyMessageId="copy"
                color="muted-foreground"
                eventName="app/report/section_copy.button/click"
              />
              {isActionAvailable && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
                  onClick={() => {
                    trackEvent({
                      eventName: "app/report/section_edit.button/click",
                      properties: {
                        sectionId: reportSection.id,
                        sectionTitle: reportSection.title,
                      },
                    });
                    setEditDialogOpen(true);
                  }}
                  disabled={isProcessing}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  <FormattedMessage id="report.edit" defaultMessage="Edit" />
                </Button>
              )}
              {isActionAvailable && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
                  onClick={() => {
                    trackEvent({
                      eventName: "app/report/section_rewrite.button/click",
                      properties: {
                        sectionId: reportSection.id,
                        sectionTitle: reportSection.title,
                      },
                    });
                    handleRegenerate();
                  }}
                  disabled={isProcessing}
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  <FormattedMessage
                    id="report.rewrite"
                    defaultMessage="Rewrite"
                  />
                </Button>
              )}
            </div>
          </div>
        )}

        {editDialogOpen && (
          <EditReportSectionDialog
            section={{
              id: reportSection.id,
              title: reportSection.title,
              content: sectionData?.content,
            }}
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            onSave={handleEdit}
          />
        )}
      </AccordionItem>
    );
  }
);
